import { Box, Loader } from '@wix/design-system';
import { getSearchParams } from '@wix/package-picker-shared';
import { Checkout as PlatformCheckout } from '@wix/premium-purchase-platform/@components';
import React, { useCallback, useState } from 'react';
import { useCurrentModule } from '../ModuleProvider';
import { CreditExplanation } from './CheckoutCreditExplanation';
import { ChangePlanCycle } from './ChangePlanCycle';
import { CheckoutFreeDomainMatchCycle } from './CheckoutFreeDomainMatchCycle';
import { useCheckoutCallbacks, useCouponLabel } from './hooks';
import { usePaymentButtonExposed } from './hooks/usePaymentButtonExposed';
import { CheckoutDisclaimer } from './CheckoutDisclaimer';
import { CheckoutTrustBuilder } from './CheckoutTrustBuilder';
import s from './Checkout.scss';
export const Checkout = _ref => {
  var _selectedCycle$cycleD, _selectedCycle$cycleD2;
  let {
    shouldOpenDoublePurchaseModal,
    isMobile = false
  } = _ref;
  const module = useCurrentModule();
  const selectedProductId = module.SDK.PackagePickerStore.useSelectedProductId();
  const selectedCycle = module.SDK.PackagePickerStore.useSelectedCycle();
  const {
    siteGuid,
    referralAdditionalInfo,
    flow
  } = getSearchParams();
  const appName = referralAdditionalInfo === 'cycle-upsale-intent' ? 'premium-purchase-plan-direct' : 'premium-purchase-plan';
  const [switchedViaBanner, setSwitchedViaBanner] = useState(false);
  const {
    experiments
  } = module.internals.experiments.hooks.useExperiments();
  const isChangePlanCycleEnabled = experiments.isEnabled('specs.premium.CheckoutYearlyUpsell');
  const isCheckoutSavingsEnabled = experiments.isEnabled('specs.premium.Checkout_Savings_PREM2-5543');
  const handleSwitchCycle = useCallback(newCycleId => {
    module.SDK.PackagePickerStore.setSelectedCycleId(newCycleId);
    setSwitchedViaBanner(prev => !prev);
  }, [module.SDK.PackagePickerStore]);
  const {
    data: offering
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOffering({});
  const {
    data: discountData
  } = module.SDK.eclipseOfferingApiClient.hooks.useFetchOfferingDiscount({});
  const showSavings = isCheckoutSavingsEnabled && !(discountData != null && discountData.hasSale);
  const checkoutCallbacks = useCheckoutCallbacks({
    shouldOpenDoublePurchaseModal,
    isMobile
  });
  const getCouponLabel = useCouponLabel();
  const {
    didInitSuccess
  } = checkoutCallbacks;
  usePaymentButtonExposed(didInitSuccess);
  if (!selectedProductId || !selectedCycle || !siteGuid) {
    return /*#__PURE__*/React.createElement(Box, {
      width: "100%",
      align: "center",
      verticalAlign: "middle",
      height: "500px"
    }, /*#__PURE__*/React.createElement(Loader, null));
  }
  const lineItem = {
    productId: selectedProductId,
    billingCycle: selectedCycle,
    quantity: 1,
    customOfferingFilters: flow ? {
      flow
    } : undefined,
    context: {
      msid: siteGuid
    }
  };
  return /*#__PURE__*/React.createElement(Box, {
    width: "100%",
    align: "center",
    verticalAlign: "middle",
    className: s.checkoutWrapper
  }, !didInitSuccess && /*#__PURE__*/React.createElement(Box, {
    align: "center",
    verticalAlign: "middle",
    height: "500px",
    width: "100%"
  }, /*#__PURE__*/React.createElement(Loader, null)), /*#__PURE__*/React.createElement(Box, {
    display: didInitSuccess ? 'block' : 'none',
    minHeight: "500px"
  }, /*#__PURE__*/React.createElement(PlatformCheckout.Root, {
    key: `${selectedProductId}-${(_selectedCycle$cycleD = selectedCycle.cycleDuration) == null ? void 0 : _selectedCycle$cycleD.count}-${(_selectedCycle$cycleD2 = selectedCycle.cycleDuration) == null ? void 0 : _selectedCycle$cycleD2.unit}`,
    disclaimerRenderer: () => /*#__PURE__*/React.createElement(Box, {
      margin: "SP2 0 SP3 0"
    }, /*#__PURE__*/React.createElement(CheckoutDisclaimer, {
      isMobile: isMobile
    })),
    lineItems: [lineItem],
    checkoutProps: {
      appName,
      onBeforeSubmit: checkoutCallbacks.getShouldSubmit,
      onSubmitSuccess: checkoutCallbacks.handleSubmitSuccess,
      onInitSuccess: checkoutCallbacks.handleInitSuccess,
      onInitError: checkoutCallbacks.handleInitError,
      onSubmitError: checkoutCallbacks.handleSubmitError,
      summaryProps: {
        injectedCouponDisplayName: getCouponLabel,
        renderCustomFooter: () => /*#__PURE__*/React.createElement(CheckoutTrustBuilder, null),
        renderCreditExplanation: () => /*#__PURE__*/React.createElement(CreditExplanation, null),
        renderAfterDiscount: () => /*#__PURE__*/React.createElement(CheckoutFreeDomainMatchCycle, null),
        renderItems: (items, formatCurrency) => {
          return items.map(item => /*#__PURE__*/React.createElement(React.Fragment, {
            key: item.productId
          }, /*#__PURE__*/React.createElement(PlatformCheckout.SummaryItem, {
            lineItem: lineItem,
            purchaseUserOffering: offering ?? {},
            summaryItem: item,
            formatCurrency: formatCurrency,
            saleBadgeProps: showSavings ? {
              badgeProps: {
                className: s.saveBadge
              }
            } : {
              boxProps: {
                style: {
                  display: 'none'
                }
              }
            },
            priceProps: {
              hasSavings: showSavings
            }
          }), isChangePlanCycleEnabled && /*#__PURE__*/React.createElement(ChangePlanCycle, {
            switchedViaBanner: switchedViaBanner,
            onSwitchCycle: handleSwitchCycle
          })));
        }
      }
    }
  })));
};
//# sourceMappingURL=Checkout.js.map